import React from 'react';
import { motion } from 'motion/react';
import { Bell, CalendarClock, Lock, ShoppingBag, ShoppingCart, Sparkles, Store } from 'lucide-react';
import { dur, drawStroke, ease, lift, loop, pop, stagger } from '@/lib/motion';
import { ShoppingBreakPhone } from './ShoppingBreakPhone';

/**
 * Feature visuals for the value-based onboarding screens — one per screen, all
 * rendered as light cards floating on the dark `bg-growth` background (per the
 * reference design). Motion rules: tokens only, transform + opacity only, every
 * entrance honors `reduced`. Each visual gets at most ONE hero overshoot.
 */

interface VisualProps {
  reduced: boolean;
}

const CARD_SHADOW = 'shadow-[0_14px_34px_rgba(0,0,0,0.28)]';

/** Gentle ambient drift for a whole collage. No-op under reduced motion. */
const Float: React.FC<{ reduced: boolean; className?: string; children: React.ReactNode }> = ({
  reduced,
  className,
  children,
}) =>
  reduced ? (
    <div className={className}>{children}</div>
  ) : (
    <motion.div
      className={className}
      animate={{ y: [0, -4, 0] }}
      transition={{ duration: loop.slow, repeat: Infinity, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );

/* ── Screen 1: insight bubbles ─────────────────────────────────────────────── */

const INSIGHT_BARS = [0.45, 0.62, 0.5, 0.95];

export const MindfulMoneyVisual: React.FC<VisualProps> = ({ reduced }) => (
  <Float reduced={reduced} className="relative mx-auto h-[210px] w-[290px]">
    {/* Insight card — restaurant spending */}
    <motion.div
      variants={lift(reduced)}
      className={`absolute left-0 top-0 w-[205px] -rotate-2 rounded-2xl bg-cloud p-4 text-left ${CARD_SHADOW}`}
    >
      <p className="text-[11.5px] font-semibold leading-snug text-growth">
        Your restaurant spending is up this month.
      </p>
      <div className="mt-3 flex h-11 items-end gap-2">
        {INSIGHT_BARS.map((h, i) => (
          <motion.div
            key={i}
            className={`w-7 rounded-t-md ${i === INSIGHT_BARS.length - 1 ? 'bg-clay' : 'bg-stone/25'}`}
            style={{ height: `${h * 100}%`, transformOrigin: 'bottom' }}
            initial={{ scaleY: reduced ? 1 : 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: dur.slow, ease: ease.calm, delay: reduced ? 0 : 0.4 + i * stagger.base }}
          />
        ))}
        <motion.span
          initial={{ opacity: reduced ? 1 : 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: dur.base, ease: ease.calm, delay: reduced ? 0 : 0.9 }}
          className="mb-auto rounded-full bg-clay/15 px-1.5 py-0.5 text-[10px] font-semibold text-clay-dark"
        >
          +18%
        </motion.span>
      </div>
    </motion.div>

    {/* Needs / wants / regrets mini chart */}
    <motion.div
      variants={lift(reduced, { delay: 0.12 })}
      className={`absolute right-1 top-[92px] w-[168px] rotate-2 rounded-2xl bg-cloud p-3 text-left ${CARD_SHADOW}`}
    >
      <p className="text-[10px] font-medium uppercase tracking-wide text-growth/55">Last week</p>
      <div className="mt-2 flex h-2.5 gap-0.5 overflow-hidden rounded-full">
        {[
          { w: '46%', cls: 'bg-foliage' },
          { w: '34%', cls: 'bg-sky' },
          { w: '20%', cls: 'bg-clay' },
        ].map((seg, i) => (
          <motion.div
            key={i}
            className={`h-full rounded-full ${seg.cls}`}
            style={{ width: seg.w, transformOrigin: 'left' }}
            initial={{ scaleX: reduced ? 1 : 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: dur.slow, ease: ease.calm, delay: reduced ? 0 : 0.6 + i * stagger.base }}
          />
        ))}
      </div>
      <div className="mt-2 flex gap-2 whitespace-nowrap text-[9px] text-growth/65">
        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-foliage" />Needed</span>
        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-sky" />Wanted</span>
        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-clay" />Regret</span>
      </div>
    </motion.div>

    {/* Subscriptions pill */}
    <motion.div
      variants={lift(reduced, { delay: 0.24 })}
      className={`absolute bottom-0 left-2 flex items-center gap-2 rounded-full bg-cloud py-2 pl-3 pr-3.5 ${CARD_SHADOW}`}
    >
      <span className="h-2 w-2 rounded-full bg-foliage" />
      <span className="text-[11px] font-medium text-growth">
        <span className="font-semibold">$208</span>/month in subscriptions
      </span>
    </motion.div>
  </Float>
);

/* ── Screen 2: reflected transactions (plain-language verdicts) ────────────── */

/** A couple of already-reflected purchases, each with a human verdict pill. */
const REFLECTED_TXNS: {
  title: string;
  meta: string;
  verdict: string;
  tone: 'good' | 'regret';
}[] = [
  { title: 'Dinner with Mom', meta: 'Saturday · $62', verdict: 'Worth it', tone: 'good' },
  { title: 'A subscription you forgot', meta: 'Renewed today · $16', verdict: 'Not really you', tone: 'regret' },
];

export const ReflectHabitsVisual: React.FC<VisualProps> = ({ reduced }) => (
  <div className="mx-auto flex h-[210px] w-[280px] flex-col justify-center">
    <motion.div variants={lift(reduced)} className={`rounded-2xl bg-cloud p-4 text-left ${CARD_SHADOW}`}>
      {REFLECTED_TXNS.map((txn, i) => (
        <div
          key={txn.title}
          className={`flex items-center justify-between gap-3 ${i > 0 ? 'mt-3 border-t border-mist pt-3' : ''}`}
        >
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold leading-snug text-growth">{txn.title}</p>
            <p className="mt-0.5 text-[10.5px] text-growth/55">{txn.meta}</p>
          </div>
          <motion.span
            variants={pop(reduced, { delay: 0.4 + i * 0.18 })}
            className={`shrink-0 rounded-full px-3 py-1 text-[11.5px] font-medium ${
              txn.tone === 'good' ? 'bg-branch text-growth' : 'bg-clay/15 text-clay-dark'
            }`}
          >
            {txn.verdict}
          </motion.span>
        </div>
      ))}
    </motion.div>
  </div>
);

/* ── Screen 3: shopping break ──────────────────────────────────────────────── */

/**
 * Animated variant — reuses the pause page's looping phone demo (pick a window
 * → tap Start → habit opens a shopping app → enough's shield steps in), scaled
 * to fit the onboarding visual slot. The static `ShoppingBreakVisual` card below
 * is kept as plan B: swap the screen's `Visual` back to it in one line.
 */
export const ShoppingBreakAnimatedVisual: React.FC<VisualProps> = () => (
  <div className="mx-auto flex w-[280px] justify-center">
    <ShoppingBreakPhone />
  </div>
);

const BLOCKED_APPS = [ShoppingBag, ShoppingCart, Store];

export const ShoppingBreakVisual: React.FC<VisualProps> = ({ reduced }) => (
  <div className="mx-auto flex h-[210px] w-[280px] items-center justify-center">
    <motion.div
      variants={lift(reduced)}
      className={`flex w-full flex-col items-center rounded-2xl bg-cloud px-5 py-4 ${CARD_SHADOW}`}
    >
      <div className="relative h-[96px] w-[96px]">
        <svg viewBox="0 0 96 96" className="h-full w-full -rotate-90">
          <circle cx="48" cy="48" r="41" className="text-mist" stroke="currentColor" strokeWidth="6" fill="none" />
          <motion.circle
            cx="48"
            cy="48"
            r="41"
            className="text-foliage"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            fill="none"
            initial={{ pathLength: reduced ? 0.72 : 0 }}
            animate={{ pathLength: 0.72 }}
            transition={{ ...drawStroke(reduced, dur.ambient), delay: reduced ? 0 : 0.35 }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
          <span className="flex gap-1" aria-hidden>
            <span className="h-4 w-[5px] rounded-full bg-growth" />
            <span className="h-4 w-[5px] rounded-full bg-growth" />
          </span>
          <span className="text-[11px] font-semibold text-growth">24h left</span>
        </div>
      </div>

      <p className="mt-2.5 text-[12px] font-semibold text-growth">Shopping break — on</p>

      <div className="mt-3 flex gap-2.5">
        {BLOCKED_APPS.map((Icon, i) => (
          <div key={i} className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-sand">
            <Icon size={17} className="text-growth/35" aria-hidden />
            <motion.span
              variants={pop(reduced, { delay: 0.9 + i * 0.14 })}
              className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-growth"
            >
              <Lock size={9} className="text-branch" aria-hidden />
            </motion.span>
          </div>
        ))}
      </div>
    </motion.div>
  </div>
);

/* ── Screen 4: goal achieved ───────────────────────────────────────────────── */

export const GoalAchievedVisual: React.FC<VisualProps> = ({ reduced }) => (
  <div className="mx-auto flex h-[210px] w-[280px] items-center justify-center">
    <motion.div variants={lift(reduced)} className={`w-full rounded-2xl bg-cloud p-5 text-left ${CARD_SHADOW}`}>
      <div className="flex items-center gap-3">
        <motion.span
          variants={pop(reduced, { delay: 0.4 })}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sage/15"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden className="text-sage-dark">
            <motion.path
              d="m5.5 12.5 4.2 4.2 8.8-9.4"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: reduced ? 1 : 0 }}
              animate={{ pathLength: 1 }}
              transition={{ ...drawStroke(reduced, dur.slow), delay: reduced ? 0 : 0.6 }}
            />
          </svg>
        </motion.span>
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-growth/55">Goal achieved</p>
          <p className="text-[14px] font-semibold leading-snug text-growth">Reduce regretted spending</p>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-baseline justify-between text-[11px] text-growth/60">
          <span>This month</span>
          <span className="text-[13px] font-semibold text-sage-dark">− 5%</span>
        </div>
        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-mist">
          <motion.div
            className="h-full rounded-full bg-sage"
            style={{ transformOrigin: 'left' }}
            initial={{ scaleX: reduced ? 1 : 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: dur.ambient, ease: ease.calm, delay: reduced ? 0 : 0.5 }}
          />
        </div>
        <p className="mt-3 text-[11.5px] leading-snug text-growth/65">
          Less regret, more $$ left for what you love.
        </p>
      </div>
    </motion.div>
  </div>
);

/* ── Screen 5: free-trial timeline ─────────────────────────────────────────── */

/**
 * Trial terms differ by store rules: Apple/Google express intro offers in
 * MONTHS, so native copy must say "1 month" and can't pin the reminder to a
 * day number ("Day 28"); web (Stripe `trial_period_days`) says 30 days.
 */
const TRIAL_STOPS = {
  native: [
    { Icon: Sparkles, day: 'Today', text: 'Get connected and start exploring.' },
    { Icon: Bell, day: '2 days before it ends', text: 'You\'re reminded before your trial ends.' },
    { Icon: CalendarClock, day: 'After 1 month', text: 'You\'re in! Membership renews until you cancel.' },
  ],
  web: [
    { Icon: Sparkles, day: 'Today', text: 'Get connected and start exploring.' },
    { Icon: Bell, day: 'Day 28', text: 'You\'re reminded before your trial ends.' },
    { Icon: CalendarClock, day: 'Day 30', text: 'You\'re in! Membership renews until you cancel.' },
  ],
} as const;

/**
 * Stops for someone the store will NOT be giving a trial to (their store
 * account already used it). Same visual language, minus the free period — the
 * timeline above would otherwise promise a trial the copy has already, and
 * correctly, stopped promising.
 */
const MEMBER_STOPS = [
  { Icon: Sparkles, day: 'Today', text: 'Get connected and start exploring.' },
  { Icon: Bell, day: 'Anytime', text: 'Cancel in a couple of taps — no lock-in.' },
  { Icon: CalendarClock, day: 'Ongoing', text: 'You\'re in! Membership renews until you cancel.' },
] as const;

export type TrialPlatform = keyof typeof TRIAL_STOPS;

export const TrialTimelineVisual: React.FC<
  VisualProps & { platform?: TrialPlatform; hasTrial?: boolean }
> = ({
  reduced,
  platform = 'native',
  hasTrial = true,
}) => (
  <div className="mx-auto flex h-[210px] w-[280px] items-center justify-center">
    <motion.div variants={lift(reduced)} className={`w-full rounded-2xl bg-cloud px-5 py-4 text-left ${CARD_SHADOW}`}>
      <div className="relative">
        <motion.span
          aria-hidden
          className="absolute bottom-4 left-[15px] top-4 w-[2px] rounded-full bg-foliage/30"
          style={{ transformOrigin: 'top' }}
          initial={{ scaleY: reduced ? 1 : 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: dur.ambient, ease: ease.calm, delay: reduced ? 0 : 0.45 }}
        />
        {(hasTrial ? TRIAL_STOPS[platform] : MEMBER_STOPS).map(({ Icon, day, text }, i) => (
          <motion.div
            key={day}
            className="relative flex items-center gap-3 py-2.5"
            initial={{ opacity: reduced ? 1 : 0, y: reduced ? 0 : 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: dur.base, ease: ease.calm, delay: reduced ? 0 : 0.35 + i * 0.22 }}
          >
            <span
              className={`z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                i === 0 ? 'bg-foliage text-growth' : 'border border-mist bg-sand text-growth/60'
              }`}
            >
              <Icon size={14} aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-growth">{day}</p>
              <p className="text-[11px] leading-snug text-growth/65">{text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  </div>
);
